import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { UserNameCell } from '@/components/core';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';
import { cn } from '@/lib/utils';
import { dateOnlyOf, formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import {
  LeaveCalendarEmployeeGroup,
  LeaveCalendarHolidayEvent,
  LeaveCalendarLeaveEvent,
} from '../../types/leave-calendar.types';
import { daySpanOf, formatDayLabel } from '../../utils/leave-calendar-week';

interface LeaveCalendarGridProps {
  /** The days shown as columns ("YYYY-MM-DD"): a week, or a whole month as a compact timeline. */
  days: string[];
  /** Today in the organization's time zone, highlighted when it is one of `days`. */
  today: string;
  employees: LeaveCalendarEmployeeGroup[];
  holidays: LeaveCalendarHolidayEvent[];
  isLoading: boolean;
  /** Shown when no one is on leave in `days`. */
  emptyMessage: string;
  /** The employee's email by IAM user id (a group's `ItemId`), shown under the name. */
  emailOf?: (userId: string) => string | undefined;
}

const LEAVE_TYPE_KEYS: Record<string, string> = { FullDay: 'FULL_DAY', HalfDay: 'HALF_DAY' };

/** More days than a week turn the grid into a timeline: narrow columns, one-line cards. */
const WEEK_LENGTH = 7;

const WithTooltip = ({ content, children }: { content?: ReactNode; children: ReactNode }) =>
  content ? (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  ) : (
    children
  );

const LeaveCard = ({
  leave,
  from,
  to,
  compact,
}: {
  leave: LeaveCalendarLeaveEvent;
  from: number;
  to: number;
  compact: boolean;
}) => {
  const { t } = useTranslation();
  const typeKey = leave.LeaveType ? LEAVE_TYPE_KEYS[leave.LeaveType] : undefined;
  const range = `${formatDateOnly(leave.StartDate)} - ${formatDateOnly(leave.EndDate)}`;
  const name = leave.LeaveCategory || leave.Title;

  return (
    <WithTooltip
      content={
        <>
          <p className="font-medium">{name}</p>
          <p>{range}</p>
          {typeKey && <p>{t(typeKey)}</p>}
        </>
      }
    >
      <div
        className={cn(
          'min-w-0 rounded-md border-l-4 border-primary-600 bg-primary-50 text-xs',
          compact ? 'mx-0.5 my-0.5 px-1 py-0.5' : 'mx-1 my-0.5 px-2 py-1'
        )}
        style={{ gridColumn: `${from + 1} / ${to + 2}` }}
      >
        <p className="truncate font-medium text-primary-700">{name}</p>
        {!compact && (
          <p className="truncate text-muted-foreground">{typeKey ? t(typeKey) : range}</p>
        )}
      </div>
    </WithTooltip>
  );
};

/**
 * Leave over a run of days: one row per employee, one column per day, each leave a card spanning the
 * days it covers (clipped to the range). Holidays tint their column and are named in its header (in a
 * tooltip on the compact month timeline).
 */
export const LeaveCalendarGrid = ({
  days,
  today,
  employees,
  holidays,
  isLoading,
  emptyMessage,
  emailOf,
}: LeaveCalendarGridProps) => {
  const { t, i18n } = useTranslation();
  const compact = days.length > WEEK_LENGTH;
  const dayColumns = `repeat(${days.length}, minmax(0, 1fr))`;
  // The names column stays in place while the days scroll sideways. A phone gets the mobile view.
  const nameWidth = compact ? '12rem' : '14rem';
  const fullRow = { gridColumn: '1 / -1' };

  const holidaysOn = (day: string) =>
    holidays.filter((holiday) => dateOnlyOf(holiday.StartDate) === day);
  const dayTint = (day: string) =>
    cn(day === today && 'bg-primary-50/60', holidaysOn(day).length > 0 && 'bg-muted');

  return (
    <TooltipProvider>
      <div className="overflow-x-auto rounded-md border">
        <div
          className="grid"
          style={{
            gridTemplateColumns: `${nameWidth} ${dayColumns}`,
            minWidth: compact ? `calc(${nameWidth} + ${days.length * 2}rem)` : '56rem',
          }}
        >
          <div className="sticky left-0 z-10 flex items-end bg-background px-3 py-2 text-sm font-medium text-muted-foreground">
            {t('EMPLOYEES')}
          </div>
          {days.map((day) => {
            const dayHolidays = holidaysOn(day);
            return (
              <WithTooltip
                key={day}
                content={
                  compact && dayHolidays.length > 0
                    ? dayHolidays.map((holiday) => <p key={holiday.ItemId}>{holiday.Name}</p>)
                    : undefined
                }
              >
                <div
                  className={cn(
                    'border-l py-2',
                    compact ? 'px-0.5 text-center' : 'px-2',
                    dayTint(day)
                  )}
                >
                  <p
                    className={cn(
                      'text-xs text-muted-foreground',
                      day === today && 'font-medium text-primary-600'
                    )}
                  >
                    {formatDayLabel(day, i18n.language, {
                      weekday: compact ? 'narrow' : 'short',
                    })}
                  </p>
                  <p className={cn('text-sm font-medium', day === today && 'text-primary-600')}>
                    {formatDayLabel(
                      day,
                      i18n.language,
                      compact ? { day: 'numeric' } : { day: 'numeric', month: 'short' }
                    )}
                  </p>
                  {!compact &&
                    dayHolidays.map((holiday) => (
                      <p key={holiday.ItemId} className="truncate text-xs text-muted-foreground">
                        {holiday.Name}
                      </p>
                    ))}
                </div>
              </WithTooltip>
            );
          })}

          {isLoading &&
            Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="border-t p-2" style={fullRow}>
                <Skeleton className="h-10 w-full" />
              </div>
            ))}

          {!isLoading && employees.length === 0 && (
            <p className="border-t py-10 text-center text-sm text-muted-foreground" style={fullRow}>
              {emptyMessage}
            </p>
          )}

          {!isLoading &&
            employees.map((employee) => (
              <div key={employee.ItemId} className="contents">
                <div className="sticky left-0 z-10 flex min-w-0 items-center border-t bg-background px-3 py-2">
                  <UserNameCell
                    name={employee.DisplayName}
                    email={employee.Email ?? emailOf?.(employee.ItemId)}
                  />
                </div>
                <div className="relative border-t" style={{ gridColumn: `span ${days.length}` }}>
                  <div
                    className="absolute inset-0 grid"
                    style={{ gridTemplateColumns: dayColumns }}
                    aria-hidden
                  >
                    {days.map((day) => (
                      <div key={day} className={cn('border-l', dayTint(day))} />
                    ))}
                  </div>
                  {/* No padding or gap, so the cards' columns line up with the day columns behind. */}
                  <div
                    className="relative grid min-h-12 grid-flow-row-dense py-0.5"
                    style={{ gridTemplateColumns: dayColumns }}
                  >
                    {employee.LeaveEvents.map((leave) => {
                      const span = daySpanOf(leave.StartDate, leave.EndDate, days);
                      return span ? (
                        <LeaveCard key={leave.ItemId} leave={leave} compact={compact} {...span} />
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </TooltipProvider>
  );
};
